import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import {
  Button,
  EmptyState,
  ErrorPanel,
  PageHeader,
  SkeletonRows,
} from "@core/components";
import { hasAction } from "@core/permissions";
import { isArchived, isVisibleToViewer } from "../../_shared";
import { CreateCohortDialog } from "../../create";
import { CohortRow, useCohorts } from "../../list";
import { useRestoreCohort } from "../../manage";

/**
 * « Cohortes » (Figma "Cohorts empty"): the title, « Nouvelle cohorte » on the right, and the
 * list. With no cohort the list is a dashed card with an illustration and one line.
 *
 * « Nouvelle cohorte » shows only when the server says this account may create (page-level
 * `available_actions.create`, true for the President); the API refuses anyone else. It opens the
 * create dialog; once the cohort exists its page opens. An Administrator sees only the cohorts
 * they are responsible for, so their empty state says a cohort has not been assigned to them yet,
 * with no button.
 */
export function CohortsScreen() {
  const { t } = useTranslation("cohorts");
  const navigate = useNavigate();
  const cohorts = useCohorts();
  const restore = useRestoreCohort();
  const [isCreating, setCreating] = useState(false);
  const canCreate = hasAction(cohorts.data?.available_actions, "create");

  // Archived cohorts are shown only to the account that can restore them.
  const results = (cohorts.data?.results ?? []).filter(isVisibleToViewer);
  const groups = [
    {
      id: "active",
      title: t("list.in-progress"),
      cohorts: results.filter((cohort) => !isArchived(cohort)),
    },
    {
      id: "archived",
      title: t("list.archived"),
      cohorts: results.filter((cohort) => isArchived(cohort)),
    },
  ].filter((group) => group.cohorts.length > 0);

  return (
    <>
      <PageHeader
        title={t("title")}
        actions={
          canCreate ? (
            <Button
              onPress={() => {
                setCreating(true);
              }}
            >
              <Icon name="plus" size={16} />
              {t("new")}
            </Button>
          ) : null
        }
      />

      {cohorts.isPending ? (
        <SkeletonRows rows={3} />
      ) : cohorts.isError ? (
        <ErrorPanel
          isRetrying={cohorts.isFetching}
          onRetry={() => {
            void cohorts.refetch();
          }}
        />
      ) : results.length === 0 ? (
        <EmptyState
          dashed
          illustration="cohorts"
          title={canCreate ? t("empty.none") : t("empty.assigned")}
          className="min-h-80 justify-center"
        />
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.id} className="flex flex-col gap-sm">
              <h2 className="flex items-center gap-xs text-sm-semibold text-(--text-primary)">
                {group.title}
                <span className="rounded-full bg-ink-100 px-xs text-xs-regular text-(--text-secondary)">
                  {group.cohorts.length}
                </span>
              </h2>
              <ul aria-label={group.title} className="flex flex-col gap-sm">
                {group.cohorts.map((cohort) => (
                  <CohortRow
                    key={cohort.id}
                    cohort={cohort}
                    onRestore={(target) => {
                      restore.mutate(target);
                    }}
                    isRestoring={
                      restore.isPending && restore.variables.id === cohort.id
                    }
                  />
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {isCreating ? (
        <CreateCohortDialog
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(cohort) => {
            setCreating(false);
            void navigate({
              to: "/cohorts/$cohortId",
              params: { cohortId: cohort.id },
            });
          }}
          onInviteAdministrator={() => {
            setCreating(false);
            void navigate({ to: "/team", search: { invite: true } });
          }}
        />
      ) : null}
    </>
  );
}
